import { ChevronLeft, ChevronRight } from 'lucide-react'
import * as React from 'react'
import { DayPicker } from 'react-day-picker'

import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { ApplicationOrientation } from '~/common/generated/apollo-operations'
import { useApplicationOrientation } from '~/common/hooks/useApplicationOrientation'

export type CalendarProps = React.ComponentProps<typeof DayPicker>

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  const { orientation } = useApplicationOrientation()
  const direction = orientation === ApplicationOrientation.RIGHT_TO_LEFT ? 'rtl' : 'ltr'

  return (
    <DayPicker
      dir={direction}
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-xs',
        nav: 'space-x-1 flex items-center',
        nav_button: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        nav_button_previous: 'absolute start-1',
        nav_button_next: 'absolute end-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex',
        head_cell: 'text-muted-foreground rounded-md w-9 font-normal text-xs',
        row: 'flex w-full mt-2',
        cell: 'h-9 w-9 text-center text-xs p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-gray-2',
        ),
        day_range_end: 'day-range-end',
        day_selected:
          'bg-gray-12 rounded text-primary-foreground hover:bg-gray-12 hover:text-primary-foreground focus:bg-gray-12 focus:text-primary-foreground',
        day_today: cn(
          'relative after:absolute after:bottom-1 after:start-1/2 after:-translate-x-1/2',
          'after:w-1 after:h-1 after:rounded-full',
          'after:bg-gray-12',
          '[&.day-selected]:after:bg-white', // White dot only when it's the selected date
          '[&.day-range-start]:after:bg-white', // White dot when it's range start
          '[&.day-range-end]:after:bg-white', // White dot when it's range end
          '[&.day-range-middle]:after:bg-gray-12', // Gray dot when it's in the middle of a range
        ),
        day_outside:
          'day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
        day_disabled: 'text-muted-foreground opacity-50',
        day_range_middle: 'aria-selected:bg-gray-2 aria-selected:text-foreground day-range-middle',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        IconLeft: ({ ...props }) => <ChevronLeft className="h-4 w-4" />,
        IconRight: ({ ...props }) => <ChevronRight className="h-4 w-4" />,
      }}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
